import {
	useQuery,
	UseQueryResult,
	useMutation,
	UseMutationResult,
} from '@tanstack/react-query'

import { SubmitSongFeedbackRequestDTO } from '@/core/api/dtos/request/playlist/submitSongFeedback'
import { SongFeedback } from '@/core/api/entities/song/songFeedback'
import { SongFeedbackMapper } from '@/core/api/mappers/song/songFeedbackMapper'

import { PaginatedResponse } from '@core/api/dtos/common/types'
import { RequestCustomPlaylistRequestDTO } from '@core/api/dtos/request/playlist/requestCustom'
import { Group } from '@core/api/entities/group'
import { Playlist } from '@core/api/entities/playlist/playlist'
import { Song } from '@core/api/entities/song/song'
import { Zone } from '@core/api/entities/zone/zone'
import { GroupMapper } from '@core/api/mappers/groupMapper'
import { PlaylistMapper } from '@core/api/mappers/playlist/playlistMapper'
import { SongMapper } from '@core/api/mappers/song/songMapper'
import { ZoneMapper } from '@core/api/mappers/zone/zoneMapper'
import { playlistService } from '@core/api/services/playlistService'
import { DEFAULT_PARAMS } from '@core/hooks/common'

/**
 * Get all playlists
 */
export const usePlaylists = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Playlist>,
	Error
> => {
	const QUERY_KEY = [
		'playlists',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService.getPlaylists(config).then(({ data: response }) => {
				return {
					data: PlaylistMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		staleTime: 5 * 60 * 1000, // 5 minutes - cache playlists for 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory for 10 minutes (previously cacheTime)
		refetchOnMount: false, // Prevent refetch on component remount
		refetchOnWindowFocus: false, // Prevent refetch on window focus
		...options,
	})
}

/**
 * Get assigned playlists
 */
export const useAssignedPlaylists = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Playlist>,
	Error
> => {
	const QUERY_KEY = [
		'playlists-assigned',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService
				.getAssignedPlaylists(config)
				.then(({ data: response }) => {
					return {
						data: PlaylistMapper.toEntities(response.data),
						meta: response.meta,
					}
				}),
		staleTime: 5 * 60 * 1000, // 5 minutes - cache playlists for 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory for 10 minutes (previously cacheTime)
		refetchOnMount: false, // Prevent refetch on component remount
		refetchOnWindowFocus: false, // Prevent refetch on window focus
		...options,
	})
}

/**
 * Get in-use playlists
 */
export const useInUsePlaylists = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Playlist>,
	Error
> => {
	const QUERY_KEY = [
		'playlists-in-use',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService.getInUsePlaylists(config).then(({ data: response }) => {
				return {
					data: PlaylistMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		staleTime: 5 * 60 * 1000, // 5 minutes - cache playlists for 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory for 10 minutes (previously cacheTime)
		refetchOnMount: false, // Prevent refetch on component remount
		refetchOnWindowFocus: false, // Prevent refetch on window focus
		...options,
	})
}

/**
 * Get favorite playlists
 */
export const useFavoritePlaylists = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Playlist>,
	Error
> => {
	const QUERY_KEY = [
		'playlists-favorites',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService
				.getFavoritePlaylists(config)
				.then(({ data: response }) => {
					return {
						data: PlaylistMapper.toEntities(response.data),
						meta: response.meta,
					}
				}),
		staleTime: 5 * 60 * 1000, // 5 minutes - cache playlists for 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory for 10 minutes (previously cacheTime)
		refetchOnMount: false, // Prevent refetch on component remount
		refetchOnWindowFocus: false, // Prevent refetch on window focus
		...options,
	})
}

/**
 * Add playlist to favorites
 */
export const useAddPlaylistToFavorites = ({
	options,
	config,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ playlistId: number; favorite: boolean },
	any
> => {
	return useMutation({
		mutationFn: ({
			playlistId,
			favorite,
		}: {
			playlistId: number
			favorite: boolean
		}) =>
			playlistService
				.addPlaylistToFavorites(playlistId, { favorite }, config)
				.then(() => {
					// Return void
				}),
		...options,
	})
}

/**
 * Request a custom playlist
 */
export const useRequestCustomPlaylist = ({
	options,
	config,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	RequestCustomPlaylistRequestDTO,
	any
> => {
	return useMutation({
		mutationFn: (data: RequestCustomPlaylistRequestDTO) =>
			playlistService.requestCustomPlaylist(data, config).then(() => {
				// Return void
			}),
		...options,
	})
}

/**
 * Get playlist details by ID
 */
export const usePlaylistById = ({
	playlistId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Playlist, Error> => {
	const QUERY_KEY = [
		'playlist',
		playlistId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService
				.getPlaylistById(playlistId, config)
				.then(({ data: response }) => {
					return PlaylistMapper.toEntity(response.data)
				}),
		retry: false, // Disable retries so 403 errors surface immediately
		enabled: playlistId !== null && playlistId !== undefined,
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Get playlist sample (preview songs)
 */
export const usePlaylistSample = ({
	playlistId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Song[], Error> => {
	const QUERY_KEY = [
		'playlist-sample',
		playlistId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService
				.getPlaylistSample(playlistId, config)
				.then(({ data: response }) => {
					return SongMapper.toEntities(response.data)
				}),
		cacheTime: 5 * 60 * 1000, // 5 minutes - cache sample for 5 minutes
		staleTime: 5 * 60 * 1000, // 5 minutes - stale sample for 5 minutes
		...options,
	})
}

export const usePlaylistFeedback = ({
	playlistId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<SongFeedback[], Error> => {
	const QUERY_KEY = [
		'playlist-feedback',
		playlistId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService
				.getPlaylistFeedback(playlistId, config)
				.then(({ data: response }) => {
					return SongFeedbackMapper.toEntities(response.data)
				}),
		...options,
	})
}

export const useSubmitSongFeedback = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	number | undefined,
	Error,
	{ playlistId: number; songId: number; data: SubmitSongFeedbackRequestDTO },
	any
> => {
	return useMutation({
		mutationFn: ({
			playlistId,
			songId,
			data,
		}: {
			playlistId: number
			songId: number
			data: SubmitSongFeedbackRequestDTO
		}) =>
			playlistService
				.submitSongFeedback(playlistId, songId, data, config)
				.then(({ data: response }) => {
					return response.data?.feedback_drop_ratio ?? undefined
				}),
		...options,
	})
}

/**
 * Get groups a playlist is assigned to
 */
export const usePlaylistGroups = ({
	playlistId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Group[], Error> => {
	const QUERY_KEY = ['playlist-groups', playlistId, config?.params]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService
				.getPlaylistGroups(playlistId, config)
				.then(({ data: response }) => {
					return GroupMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		enabled: !!playlistId,
		...options,
	})
}

/**
 * Get zones a playlist is assigned to
 */
export const usePlaylistZones = ({
	playlistId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Zone[], Error> => {
	const QUERY_KEY = ['playlist-zones', playlistId, config?.params]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			playlistService
				.getPlaylistZones(playlistId, config)
				.then(({ data: response }) => {
					return ZoneMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		enabled: !!playlistId,
		...options,
	})
}
